// ============================================
// MockDetector Beta Credits - API-Key Integration
// ============================================
// Built by Silver (Anton Fredriksson) 🇸🇪
// Now with server-side credit management + refunds
// ============================================

import { useState, useEffect } from 'react';
import { SCAN_COSTS } from '../config/scan-costs';
import { WIDGET_CONFIG } from '../config';

const STORAGE_KEY_DAILY_SCANS = 'mockdetector-daily-scans';
const STORAGE_KEY_LAST_RESET = 'mockdetector-last-reset';
const STORAGE_KEY_FEEDBACK = 'mockdetector-feedback-submitted';

interface ServerCreditsResponse {
  success: boolean;
  scanToken?: string;
  creditsRemaining?: number;
  bonusCredits?: number;
  purchasedCredits?: number;
  scansRemainingToday?: number;
  scanCost?: number;
  error?: string;
  message?: string;
}

/**
 * Hook for managing MockDetector credits (formerly useBetaCredits - kept for backwards compatibility)
 */
export const useCredits = (apiKey?: string, apiDailyScanLimit?: number, storagePrefix: string = '') => {
  // Server-side credits (source of truth when API key is provided)
  const [serverCredits, setServerCredits] = useState<number | null>(null);
  const [bonusCredits, setBonusCredits] = useState<number>(0);
  const [purchasedCredits, setPurchasedCredits] = useState<number>(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  
  // Local-only state for no-API-key mode
  const [dailyScans, setDailyScans] = useState<number>(() => getInitialDailyScans(storagePrefix));
  const maxDailyScans = apiDailyScanLimit ?? 50;

  // Feedback system state
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(`${storagePrefix}${STORAGE_KEY_FEEDBACK}`) === 'true';
  });

  // On mount, if API key exists, fetch server credits (will be updated after first scan)
  useEffect(() => {
    if (apiKey) {
      // Credits will be fetched after first scan request
      setIsLoadingCredits(true);
    }
  }, [apiKey]);

  // Check if user can scan
  const canScan = (cost: number = 1): boolean => {
    // If API key mode, check server credits (if available)
    if (apiKey) {
      if (serverCredits !== null) {
        return serverCredits >= cost && dailyScans < maxDailyScans;
      }
      // If server credits not yet fetched, allow (will be validated server-side)
      return dailyScans < maxDailyScans;
    }
    
    // Local dev mode - check daily scans only
    return dailyScans < maxDailyScans;
  };

  // ✅ NEW: Request scan from server (deducts credits server-side)
  const requestScan = async (scanType: keyof typeof SCAN_COSTS): Promise<ServerCreditsResponse> => {
    if (!apiKey) {
      // Local dev mode - just increment daily scans
      const newDailyScans = dailyScans + 1;
      setDailyScans(newDailyScans);
      localStorage.setItem(`${storagePrefix}${STORAGE_KEY_DAILY_SCANS}`, String(newDailyScans));
      
      return {
        success: true,
        scanToken: crypto.randomUUID(),
        creditsRemaining: 999, // Infinite for local dev
        scansRemainingToday: maxDailyScans - newDailyScans,
        scanCost: SCAN_COSTS[scanType]
      };
    }

    // ✅ Production mode - call server to deduct credits
    try {
      const response = await fetch(WIDGET_CONFIG.RECORD_SCAN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey,
          scanType,
          domain: window.location.hostname
        })
      });

      const data: ServerCreditsResponse = await response.json();

      if (data.success && data.creditsRemaining !== undefined) {
        // Update local state with server credits
        setServerCredits(data.creditsRemaining);
        setBonusCredits(data.bonusCredits || 0);
        setPurchasedCredits(data.purchasedCredits || 0);
        setIsLoadingCredits(false);
        
        // Increment daily scans
        const newDailyScans = dailyScans + 1;
        setDailyScans(newDailyScans);
        localStorage.setItem(`${storagePrefix}${STORAGE_KEY_DAILY_SCANS}`, String(newDailyScans));
      }

      return data;
    } catch (error) {
      console.error('Failed to request scan from server:', error);
      return {
        success: false,
        error: 'Failed to connect to server. Please try again.'
      };
    }
  };

  // Get remaining daily scans
  const remainingDailyScans = Math.max(0, maxDailyScans - dailyScans);

  // Display credits (server credits if available, otherwise "local dev mode")
  const displayCredits = serverCredits !== null 
    ? serverCredits 
    : (apiKey ? 'Loading...' : 'Unlimited (Dev)');

  // Feedback system
  const feedbackBonus = 80;
  const initialCredits = 50;

  const canSubmitFeedback = (): boolean => {
    return !feedbackSubmitted && apiKey !== undefined;
  };

  const submitFeedback = async (feedbackText: string): Promise<{success: boolean; error?: string}> => {
    if (!apiKey) {
      return { success: false, error: 'API key required for feedback' };
    }

    if (feedbackSubmitted) {
      return { success: false, error: 'Feedback already submitted' };
    }

    try {
      const response = await fetch(WIDGET_CONFIG.SUBMIT_FEEDBACK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey, feedbackText })
      });

      const data = await response.json();

      if (data.success) {
        setFeedbackSubmitted(true);
        localStorage.setItem(`${storagePrefix}${STORAGE_KEY_FEEDBACK}`, 'true');
        
        // Update credits immediately
        if (data.newCreditsBalance !== undefined) {
          setServerCredits(data.newCreditsBalance);
        }

        return { success: true };
      } else {
        return { success: false, error: data.error || data.message };
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // ⚠️ DEPRECATED: Old methods kept for backwards compatibility
  const deductCredit = (_cost: number) => true; // Always succeed since server handles it
  const setCustomCredits = (_amount: number) => {};
  const isAdminMode = false;
  const enableAdminMode = (_password: string) => false;

  /**
   * 🆕 PHASE B: Report scan result to server for credit refund
   * Call this after scan completes with the number of errors found
   */
  const reportScanResult = async (
    scanToken: string | undefined,
    errorsFound: number,
    scanType: keyof typeof SCAN_COSTS
  ): Promise<void> => {
    if (!apiKey || !scanToken) {
      return; // No API key or token, skip reporting
    }

    try {
      const response = await fetch(WIDGET_CONFIG.REPORT_SCAN_RESULT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scanToken,
          errorsFound,
          scanType,
          apiKey
        })
      });

      const data = await response.json();

      if (data.success && data.creditsRefunded) {
        // Update local balance with refunded credits
        setServerCredits(data.creditsBalance);
        console.log(`✅ ${data.refundAmount} credits refunded (no errors found)`);
      }
    } catch (error) {
      console.error('Error reporting scan result:', error);
    }
  };

  return {
    credits: displayCredits,
    serverCredits,
    bonusCredits,
    purchasedCredits,
    dailyScans,
    remainingDailyScans,
    canScan,
    requestScan, // ✅ Main scan request method
    reportScanResult, // 🆕 PHASE B: Report scan results for refunds
    maxDailyScans,
    scanCosts: SCAN_COSTS,
    isLoadingCredits,
    // Deprecated methods for backwards compatibility
    feedbackSubmitted,
    canSubmitFeedback,
    submitFeedback,
    deductCredit,
    feedbackBonus,
    setCustomCredits,
    initialCredits,
    isAdminMode,
    enableAdminMode
  };
};

// Export both names for backwards compatibility
export { useCredits as useBetaCredits };

// Helper functions
function getInitialDailyScans(storagePrefix: string = ''): number {
  if (typeof window === 'undefined') return 0;
  
  try {
    const lastReset = localStorage.getItem(`${storagePrefix}${STORAGE_KEY_LAST_RESET}`);
    const today = new Date().toDateString();
    
    if (lastReset !== today) {
      localStorage.setItem(`${storagePrefix}${STORAGE_KEY_LAST_RESET}`, today);
      localStorage.setItem(`${storagePrefix}${STORAGE_KEY_DAILY_SCANS}`, '0');
      return 0;
    }
    
    const stored = localStorage.getItem(`${storagePrefix}${STORAGE_KEY_DAILY_SCANS}`);
    return stored !== null ? parseInt(stored, 10) : 0;
  } catch (e) {
    return 0;
  }
}
